<script lang="ts">
// Module-scoped state shared across every BaseModal instance:
//  - modalIdCounter → unique aria-labelledby ids
//  - openModalCount → body scroll-lock refcount + z-index stacking for nesting
let modalIdCounter = 0;
let openModalCount = 0;
</script>

<script setup lang="ts">
import { ref, watch, nextTick, onBeforeUnmount, useSlots } from 'vue';

const props = withDefaults(
  defineProps<{
    /** Controls visibility. */
    show: boolean;
    /** Header title (also used for aria-labelledby). */
    title?: string;
    /** Optional bootstrap-icon class for the title, e.g. 'bi-briefcase'. */
    titleIcon?: string;
    /** Max-width preset. */
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen';
    /** When true, disables close affordances (Escape/backdrop/X) while an action runs. */
    loading?: boolean;
    /** Close when the backdrop is clicked. */
    closeOnBackdrop?: boolean;
    /** Close when Escape is pressed. */
    closeOnEscape?: boolean;
    /** Scroll the body when content overflows (vs. the whole page). */
    scrollable?: boolean;
    /** Hide the whole header row. */
    hideHeader?: boolean;
    /** Hide just the close (X) button. */
    hideClose?: boolean;
    /** Let the user move the dialog by dragging its header (ignored for
     *  fullscreen and for touch pointers). Position resets each time the
     *  modal opens. ON by default — pass :draggable="false" to opt out. */
    draggable?: boolean;
  }>(),
  {
    size: 'md',
    loading: false,
    closeOnBackdrop: true,
    closeOnEscape: true,
    scrollable: true,
    hideHeader: false,
    hideClose: false,
    draggable: true,
  },
);

const emit = defineEmits<{ (e: 'close'): void }>();
const slots = useSlots();

const titleId = `rw-modal-title-${++modalIdCounter}`;
const dialogRef = ref<HTMLElement | null>(null);
const zIndex = ref(1060);

let previouslyFocused: HTMLElement | null = null;
let isOpen = false;
// Position in the open-modal stack (1-based). Only the topmost modal handles
// Escape/Tab so a nested modal's Escape doesn't also close its parent.
let stackIndex = 0;

function requestClose() {
  if (props.loading) return;
  emit('close');
}

// Backdrop dismissal: close only when BOTH mousedown and mouseup land on the
// backdrop itself. A plain @click.self would also fire when a drag-select
// starts inside the dialog and ends on the backdrop — losing form input.
let backdropMouseDownSelf = false;
function onBackdropMouseDown(e: MouseEvent) {
  backdropMouseDownSelf = e.target === e.currentTarget;
}
function onBackdropMouseUp(e: MouseEvent) {
  const upSelf = e.target === e.currentTarget;
  if (backdropMouseDownSelf && upSelf && props.closeOnBackdrop) requestClose();
  backdropMouseDownSelf = false;
}

// ── Drag-to-move (opt-in via `draggable`) ────────────────────────────────────
const dragX = ref(0);
const dragY = ref(0);
let dragging = false;
let dragStartX = 0;
let dragStartY = 0;
let dragBaseX = 0;
let dragBaseY = 0;
/** The dialog's rect with the current translate removed — the fixed reference
 *  the viewport clamp works against. */
let dragBaseRect: DOMRect | null = null;

const canDrag = () => props.draggable && props.size !== 'fullscreen';

function onHeaderPointerDown(e: PointerEvent) {
  if (!canDrag() || !dialogRef.value) return;
  // Touch keeps its native behaviour (scroll/tap) — dragging is a mouse/pen
  // affordance; on phones the modals are near full-width anyway.
  if (e.pointerType === 'touch') return;
  // Buttons/links/inputs in the header (incl. the X) keep their own behaviour.
  if ((e.target as HTMLElement).closest('button, a, input, select, textarea, [role="button"]')) return;
  dragging = true;
  dragStartX = e.clientX;
  dragStartY = e.clientY;
  dragBaseX = dragX.value;
  dragBaseY = dragY.value;
  const r = dialogRef.value.getBoundingClientRect();
  dragBaseRect = new DOMRect(r.x - dragX.value, r.y - dragY.value, r.width, r.height);
  window.addEventListener('pointermove', onDragMove);
  window.addEventListener('pointerup', onDragEnd);
  e.preventDefault(); // no text selection while dragging
}

function onDragMove(e: PointerEvent) {
  if (!dragging || !dragBaseRect) return;
  let x = dragBaseX + (e.clientX - dragStartX);
  let y = dragBaseY + (e.clientY - dragStartY);
  // Keep a graspable part of the dialog on-screen: ≥120px horizontally,
  // header never above the top edge nor below the bottom 60px.
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  x = Math.min(Math.max(x, -(dragBaseRect.right - 120)), vw - dragBaseRect.left - 120);
  y = Math.min(Math.max(y, -dragBaseRect.top), vh - dragBaseRect.top - 60);
  dragX.value = x;
  dragY.value = y;
}

function onDragEnd() {
  dragging = false;
  window.removeEventListener('pointermove', onDragMove);
  window.removeEventListener('pointerup', onDragEnd);
}

function focusableEls(): HTMLElement[] {
  if (!dialogRef.value) return [];
  const sel =
    'a[href], button:not([disabled]), textarea:not([disabled]), ' +
    'input:not([disabled]):not([type=hidden]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return Array.from(dialogRef.value.querySelectorAll<HTMLElement>(sel)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

function onKeydown(e: KeyboardEvent) {
  // Ignore unless this modal is the topmost one open (nested-modal safety).
  if (stackIndex !== openModalCount) return;
  if (e.key === 'Escape' && props.closeOnEscape) {
    e.stopPropagation();
    requestClose();
    return;
  }
  if (e.key !== 'Tab' || !dialogRef.value) return;
  // Focus trap
  const els = focusableEls();
  if (els.length === 0) {
    e.preventDefault();
    dialogRef.value.focus();
    return;
  }
  const first = els[0];
  const last = els[els.length - 1];
  const active = document.activeElement as HTMLElement;
  const inside = dialogRef.value.contains(active);
  if (e.shiftKey && (active === first || !inside)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && (active === last || !inside)) {
    e.preventDefault();
    first.focus();
  }
}

async function open() {
  if (isOpen) return;
  isOpen = true;
  dragX.value = 0;
  dragY.value = 0;
  previouslyFocused = document.activeElement as HTMLElement | null;
  openModalCount += 1;
  stackIndex = openModalCount;
  if (openModalCount === 1) document.body.style.overflow = 'hidden';
  zIndex.value = 1050 + openModalCount * 10;
  document.addEventListener('keydown', onKeydown, true);
  await nextTick();
  const els = focusableEls();
  (els[0] || dialogRef.value)?.focus();
}

function close() {
  if (!isOpen) return;
  isOpen = false;
  onDragEnd();
  document.removeEventListener('keydown', onKeydown, true);
  openModalCount = Math.max(0, openModalCount - 1);
  stackIndex = 0;
  if (openModalCount === 0) document.body.style.overflow = '';
  previouslyFocused?.focus?.();
  previouslyFocused = null;
}

watch(() => props.show, (show) => (show ? open() : close()), { immediate: true });
onBeforeUnmount(() => close());
</script>

<template>
  <Teleport to="body">
    <Transition name="rw-modal">
      <div
        v-if="show"
        class="rw-modal-backdrop"
        :class="{ 'rw-modal-backdrop--flush': size === 'fullscreen' }"
        :style="{ zIndex }"
        @mousedown="onBackdropMouseDown"
        @mouseup="onBackdropMouseUp"
      >
        <div
          ref="dialogRef"
          class="rw-modal-dialog"
          :class="[`rw-modal-${size}`, { 'rw-modal-scrollable': scrollable }]"
          :style="dragX || dragY ? { transform: `translate(${dragX}px, ${dragY}px)` } : undefined"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="title ? titleId : undefined"
          tabindex="-1"
        >
          <div class="modal-content rw-modal-content">
            <!-- Header -->
            <div
              v-if="!hideHeader"
              class="modal-header rw-modal-header"
              :class="{ 'rw-modal-header--draggable': canDrag() }"
              @pointerdown="onHeaderPointerDown"
            >
              <slot name="header">
                <h5 :id="titleId" class="modal-title mb-0">
                  <i v-if="titleIcon" :class="['bi', titleIcon, 'me-2']"></i>{{ title }}
                </h5>
              </slot>
              <button
                v-if="!hideClose"
                type="button"
                class="btn-close"
                :disabled="loading"
                aria-label="Close"
                @click="requestClose"
              ></button>
            </div>

            <!-- Body -->
            <div class="modal-body rw-modal-body">
              <slot />
            </div>

            <!-- Footer -->
            <div v-if="slots.footer" class="modal-footer rw-modal-footer">
              <slot name="footer" />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.rw-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.75rem;
}
.rw-modal-backdrop--flush {
  padding: 0;
}

.rw-modal-dialog {
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 3.5rem);
  outline: none;

  /* Bootstrap styles .modal-content/.modal-header/.modal-body/.modal-footer via
     --bs-modal-* variables that are normally declared on a .modal ancestor.
     BaseModal has no .modal wrapper, so declare them here (chained to root
     tokens so light AND dark mode resolve correctly). Without these the modal
     renders transparent with zero padding. */
  --bs-modal-color: var(--bs-body-color);
  --bs-modal-bg: var(--bs-body-bg);
  --bs-modal-border-color: var(--bs-border-color-translucent);
  --bs-modal-border-width: var(--bs-border-width);
  --bs-modal-border-radius: var(--bs-border-radius-lg);
  --bs-modal-inner-border-radius: calc(var(--bs-border-radius-lg) - var(--bs-border-width));
  --bs-modal-padding: 1rem;
  --bs-modal-header-padding-x: 1rem;
  --bs-modal-header-padding-y: 1rem;
  --bs-modal-header-padding: 1rem 1rem;
  --bs-modal-header-border-color: var(--bs-border-color);
  --bs-modal-header-border-width: var(--bs-border-width);
  --bs-modal-title-line-height: 1.5;
  --bs-modal-footer-gap: 0.5rem;
  --bs-modal-footer-border-color: var(--bs-border-color);
  --bs-modal-footer-border-width: var(--bs-border-width);
}
.rw-modal-sm { max-width: 380px; }
.rw-modal-md { max-width: 520px; }
.rw-modal-lg { max-width: 820px; }
.rw-modal-xl { max-width: 1140px; }
.rw-modal-fullscreen {
  max-width: 100%;
  width: 100%;
  height: 100%;
  max-height: 100vh;
}

/* Use Bootstrap's .modal-content theme tokens so dark mode is automatic;
   explicit background here as a hard guarantee against transparency. */
.rw-modal-content {
  display: flex;
  flex-direction: column;
  max-height: 100%;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  background-color: var(--bs-body-bg);
  color: var(--bs-body-color);
}
.rw-modal-fullscreen .rw-modal-content {
  border-radius: 0;
  height: 100%;
}

.rw-modal-header,
.rw-modal-footer {
  flex-shrink: 0;
}
.rw-modal-header--draggable {
  cursor: move;
  user-select: none;
  touch-action: none;
}
.rw-modal-footer {
  background: var(--bs-tertiary-bg);
}

/* Body scrolls; header/footer stay pinned. */
.rw-modal-scrollable .rw-modal-body {
  overflow-y: auto;
}

/* Transition */
.rw-modal-enter-active,
.rw-modal-leave-active {
  transition: opacity 0.2s ease;
}
.rw-modal-enter-active .rw-modal-dialog,
.rw-modal-leave-active .rw-modal-dialog {
  transition: transform 0.2s ease;
}
.rw-modal-enter-from,
.rw-modal-leave-to {
  opacity: 0;
}
.rw-modal-enter-from .rw-modal-dialog,
.rw-modal-leave-to .rw-modal-dialog {
  transform: scale(0.96) translateY(-8px);
}
</style>
